import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cross, Star, Heart, Music, BookOpen, Bird, Mail, Youtube } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const navItems = [
    { path: '/despre', label: 'Despre', icon: Heart },
    { path: '/episoade', label: 'Episoade', icon: Music },
    { path: '/studii', label: 'Studii', icon: BookOpen },
    { path: '/rugaciuni', label: 'Rugăciuni', icon: Bird },
    { path: '/contact', label: 'Contact', icon: Mail }
  ];

  return (
    <nav className="fixed top-0 w-full z-50 bg-slate-900/90 backdrop-blur-xl border-b border-amber-400/20">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex justify-between items-center">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center space-x-2 sm:space-x-3"
          >
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3">
              <div className="relative">
                <Cross className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" />
                <Star className="w-3 h-3 sm:w-4 sm:h-4 text-amber-300 absolute -top-1 -right-1" />
              </div>
              <div>
                <h1 className="text-sm sm:text-xl font-bold text-amber-400">Cuvânt și Rugăciune</h1>
                <p className="text-xs text-slate-300">cu Mircea Stanciu</p>
              </div>
            </Link>
          </motion.div>
          
          <div className="hidden md:flex space-x-6 lg:space-x-8">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`transition-colors flex items-center space-x-1 ${
                  isActive(path) 
                    ? 'text-amber-400' 
                    : 'text-slate-300 hover:text-amber-400'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </Link>
            ))}
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-amber-400 p-3 relative z-[70] bg-slate-800/80 rounded-lg border border-amber-400/30 hover:bg-slate-700/80 transition-all"
            >
              <div className="w-6 h-6 flex flex-col justify-center space-y-1">
                <motion.span 
                  animate={isMobileMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                  className="block w-6 h-0.5 bg-amber-400 origin-center"
                />
                <motion.span 
                  animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                  className="block w-6 h-0.5 bg-amber-400"
                />
                <motion.span 
                  animate={isMobileMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                  className="block w-6 h-0.5 bg-amber-400 origin-center"
                />
              </div>
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] md:hidden"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 mobile-menu-backdrop bg-slate-900"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Menu Content */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 120 }}
              className="absolute right-0 top-0 h-full w-80 border-l-2 border-amber-400/60 shadow-2xl mobile-menu-panel bg-slate-900"
            >
              <div className="p-6 pt-20 bg-slate-900 w-full">
                <nav className="space-y-4 bg-slate-900 p-4 rounded-lg">
                  {navItems.map(({ path, label, icon: Icon }) => (
                    <motion.div key={path}>
                      <Link
                        to={path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center space-x-4 transition-colors py-4 px-6 rounded-xl border border-transparent hover:border-amber-400/30 ${
                          isActive(path)
                            ? 'text-amber-400 bg-amber-400/20'
                            : 'text-white hover:text-amber-400 hover:bg-amber-400/20'
                        }`}
                      >
                        <Icon className="w-6 h-6" />
                        <span className="text-lg font-semibold">{label}</span>
                      </Link>
                    </motion.div>
                  ))}
                </nav>
                
                {/* Mobile Menu Footer */}
                <div className="mt-10 pt-8 border-t-2 border-amber-400/30">
                  <div className="flex justify-center space-x-6 mb-6">
                    <motion.button 
                      onClick={() => window.open('https://www.youtube.com/@mircea_stanciu', '_blank')}
                      className="p-4 bg-amber-400 rounded-xl text-slate-900 hover:bg-amber-300 transition-colors shadow-lg"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Youtube className="w-6 h-6" />
                    </motion.button>
                    
                    <motion.button 
                      onClick={() => window.location.href = 'mailto:virgilstaciu31@gmail.com'}
                      className="p-4 bg-amber-400 rounded-xl text-slate-900 hover:bg-amber-300 transition-colors shadow-lg"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Mail className="w-6 h-6" />
                    </motion.button>
                    
                    <motion.button 
                      onClick={() => window.open('https://chat.whatsapp.com/DqlrnJxLjzwHKtsSAgGbaJ', '_blank')}
                      className="p-4 bg-amber-400 rounded-xl text-slate-900 hover:bg-amber-300 transition-colors shadow-lg"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Heart className="w-6 h-6" />
                    </motion.button>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-amber-400 mb-3 font-medium">Să ne rugăm împreună</p>
                    <div className="flex items-center justify-center space-x-3">
                      <Cross className="w-5 h-5 text-amber-400" />
                      <Star className="w-4 h-4 text-amber-300" />
                      <Bird className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;